import { useLocalStorage } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { Tenant } from '../data/entities/Tenant'
import { UserEntity } from '../data/entities/UserEntity'
import { EntityResponse } from '../services/api/entityResponse'
import { authedRequest, baseUrls } from '../services/api/requests'

/** List of available frontend types */
export const frontends = ['Admin', 'Tenant', 'Customer'] as const
/** Type representing the available frontend types */
export type Frontends = (typeof frontends)[number]
/** Current frontend type based on environment */
export const frontend: Frontends = import.meta.env.MODE as Frontends

const JWT_TOKEN_EXPIRE_MINUTES = 15

/**
 * Store for managing user authentication and profile information.
 * Handles JWT tokens, user details, tenant management, and authentication state.
 */
export const useUserStore = defineStore('user', () => {
  /** JWT token stored in local storage */
  const jwtToken = useLocalStorage('jwtToken', '')
  /** JWT token expiry date stored in local storage */
  const jwtTokenExpiryDate = useLocalStorage('jwtTokenExpiryDate', '')
  /** Refresh token stored in local storage */
  const refreshToken = useLocalStorage('refreshToken', '')
  /** Current tenant ID stored in local storage */
  const tenantId = useLocalStorage('tenantId', '')
  /** Last visited page stored in local storage */
  const lastPage = useLocalStorage('lastPage', '')

  // Other states
  /** Current user's login name */
  const userName = ref('john.goodenough')
  const theme = useLocalStorage('theme', 'light')
  const language = useLocalStorage('language', 'gb')
  /** Current user's userId */
  const userId = ref('')
  /** Current user's display name */
  const displayName = ref('')
  /** Current user's email address */
  const email = ref('example@example.com')
  /** Date when the user joined */
  const memberSince = ref('8/12/2020')
  /** Default profile picture path */
  const DEFAULT_PFP = `/${frontend.toLowerCase()}/default-pfp.jpg`
  /** Default tenant logo path */
  const DEFAULT_TENANT_LOGO = `/${frontend.toLowerCase()}/visfuture.png`
  /** Current user's profile picture URL */
  const pfp = ref(DEFAULT_PFP)
  /** Current tenant's logo URL */
  const tenantLogo = ref(DEFAULT_TENANT_LOGO)
  /** Whether two-factor authentication is enabled */
  const is2FAEnabled = ref(false)
  /** List of tenants the user belongs to */
  const tenantList = ref<{ id: string; name: string; logo?: string }[]>([])

  /**
   * Initializes the user store with user details from the server.
   * Loads user information, tenant logo, tenant list, and profile picture.
   */
  async function init() {
    if (!jwtToken.value || !refreshToken.value) return

    try {
      const response = await authedRequest('BaseBiz', `/${frontend}Details`, 'GET')
      const user = response.entity as UserEntity

      // Check if user data is valid
      if (!user || !user.loginName) {
        console.warn('User details not available, clearing tokens')
        clearStorage()
        return
      }

      await loadTenantLogo()
      await setTenantList()
      await updatePfp(user)

      userId.value = user.id!
      userName.value = user.loginName
      email.value = user.email
      memberSince.value = new Date(user.createdAt!).toLocaleDateString(navigator.language)
      is2FAEnabled.value = user.MFA
    } catch (error) {
      console.error('Failed to initialize user:', error)
      clearStorage()
    }
  }

  /**
   * Logs in a user with the provided credentials.
   * @param userNameInput - The username to login with
   * @param password - The password to login with
   * @returns True if login was successful, false otherwise
   */
  async function login(userNameInput: string, password: string): Promise<boolean> {
    const res = await fetch(baseUrls.BaseBiz + `/${frontend}Login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: userNameInput, password }),
    })

    const json = (await res.json()) as EntityResponse
    if (!json.isSuccess) return false

    const data = json.entity
    jwtToken.value = data.jwtToken
    jwtTokenExpiryDate.value = (Date.now() + JWT_TOKEN_EXPIRE_MINUTES * (60 * 1000)).toString()
    refreshToken.value = data.refreshToken

    return true
  }

  /**
   * Logs in a user through their microsoft account.
   * @param nonce - Unique session ID from the backend
   * @returns True if login was successful, false otherwise
   */
  async function oauthSignin(nonce: string): Promise<boolean> {
    const res = await fetch(baseUrls.BaseBiz + `/TenantSigninOauth`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nonce }),
    })

    const json = (await res.json()) as EntityResponse
    if (!json.isSuccess) return false

    const data = json.entity
    jwtToken.value = data.jwtToken
    jwtTokenExpiryDate.value = (Date.now() + JWT_TOKEN_EXPIRE_MINUTES * (60 * 1000)).toString()
    refreshToken.value = data.refreshToken

    return true
  }

  /**
   * Logs out the current user and clears all stored authentication data.
   * This reloads the page to ensure all state is cleared.
   */
  async function logout() {
    try {
      await fetch(baseUrls.BaseBiz + '/Logout', {
        method: 'GET',
        headers: { Authorization: `Bearer ${jwtToken.value}` },
      })
    } catch (e) {
      console.warn('Logout failed on server:', e)
    }

    clearStorage()

    window.location.reload()
  }

  /**
   * Sets the current tenant for the user.
   * @param _tenantId - The ID of the tenant to switch to
   * @returns True if the tenant was successfully set, false otherwise
   */
  async function setTenant(_tenantId: string): Promise<boolean> {
    let res
    if (frontend == 'Customer') {
      res = await fetch(baseUrls.BaseBiz + '/SetCustomerTenant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken.value}` },
        body: JSON.stringify({ id: _tenantId }),
      })
    } else {
      res = await fetch(baseUrls.BaseBiz + '/SetTenant', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken.value}` },
        body: JSON.stringify({ id: _tenantId }),
      })
    }

    if (res.ok) {
      tenantId.value = _tenantId
      await loadTenantLogo()
      return true
    }
    return false
  }

  /**
   * Refreshes the JWT token using the refresh token.
   * This is called when the JWT token expires.
   */
  async function refresh() {
    const res = await fetch(`${baseUrls.BaseBiz}/Refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwtToken.value}` },
      body: JSON.stringify({
        token: refreshToken.value,
        userType: frontend,
      }),
    })
    if (!res.ok) {
      await logout()
      return
    }
    const data = (await res.json()).entity
    jwtToken.value = data.jwtToken
    jwtTokenExpiryDate.value = (Date.now() + JWT_TOKEN_EXPIRE_MINUTES * (60 * 1000)).toString()
    refreshToken.value = data.refreshToken
  }

  /**
   * Loads the current tenant's logo from the server.
   */
  async function loadTenantLogo() {
    if (!tenantId.value || !jwtToken.value || !refreshToken.value) return

    const tenantResponse = await authedRequest('BaseBiz', '/GetTenant', 'GET')
    const tenant = tenantResponse.entity as Tenant

    tenantLogo.value = tenant.logoId ? baseUrls['Files'] + tenant.logoId!.substring(0, 40) : DEFAULT_TENANT_LOGO
  }

  /**
   * Sets the list of tenants the user belongs to.
   */
  async function setTenantList() {
    if (!tenantId.value || !jwtToken.value || !refreshToken.value) return

    tenantList.value = []

    const res = await authedRequest('BaseBiz', `/${frontend}TenantList`, 'GET')
    const userTenants = res.entity as Tenant[]
    for (const t of userTenants) {
      tenantList.value.push({
        id: t.id ?? '',
        name: t.name,
        logo: t.logoId ? baseUrls['Files'] + t.logoId!.substring(0, 40) : DEFAULT_TENANT_LOGO,
      })
    }
  }

  /**
   * Updates the user's profile picture.
   * @param user - Optional user entity to use for the update (if not provided, will fetch from server)
   */
  async function updatePfp(user?: UserEntity) {
    if (!user) {
      const response = await authedRequest('BaseBiz', `/${frontend}Details`, 'GET')
      user = response.entity as UserEntity
    }
    pfp.value = user.imageId ? baseUrls['Files'] + user.imageId!.substring(0, 40) : DEFAULT_PFP
  }

  /**
   * Clears all authentication-related storage.
   */
  function clearStorage() {
    jwtToken.value = ''
    refreshToken.value = ''
    tenantId.value = ''
    tenantList.value = []
  }

  return {
    // state
    jwtToken,
    jwtTokenExpiryDate,
    refreshToken,
    userName,
    userId,
    displayName,
    email,
    memberSince,
    pfp,
    is2FAEnabled,
    tenantList,
    tenantId,
    lastPage,
    tenantLogo,
    theme,
    language,

    // actions
    login,
    logout,
    setTenant,
    init,
    refresh,
    updatePfp,
    loadTenantLogo,
    clearStorage,
    oauthSignin,
  }
})
